import { ClarionButton } from '@clarion/ClarionButton';
import { ClarionTag as Tag } from '@clarion/ClarionTag';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  SearchCheck,
  Settings2,
  SquareChevronLeft,
  SquareChevronRight,
} from 'lucide-react';
import { ReactNode } from 'react';
import { Link } from 'react-router';
import { getCreatedByAssignments, getUserAssignments } from '../../assignments/assignmentService';
import { useUserState } from '../../auth/UserState';
import { useIsOrgAdmin, useUserOrganizations } from '../../auth/useUserOrganizations';
import { useWorkspaces } from '../../auth/WorkspaceState';
import { getPartnerLabel, partners, PartnerType } from '../../integrations/partner';
import useIsOperator from '../../operator/useIsOperator';
import RecapLogo from '../../shared/RecapLogo';
import { ProdWorkspaceMaintenanceBanner } from '../../shared/ProdWorkspaceMaintenanceBanner';
import { useMediaQuery } from '../../shared/useMediaQuery';
import { cx } from '../../util';
import { authFetch } from '../../util/api';
import { correctionsPath } from '../../util/correctionApi';
import FlagOverrides from '../../util/FlagOverrides';
import { getFlag } from '../../util/flags';
import { usePref } from '../../util/prefs';
import {
  useIntegrationScope,
  useIsAdmin,
  useIsContentManager,
  useIsIntegrationReviewer,
  useIsTranscriptEditor,
} from '../../workspace/permission';
import { useIsPricingAllowed } from '../../workspace/workspaceRestrictions';
import { ActiveTimerButton } from './ActiveTimerButton';
import { NavigationGroup } from './NavigationGroup';
import { NavigationItem } from './NavigationItem';
import { SideNavigationProvider, useSideNavigation } from './SideNavigationContext';
import { UserMenu } from './UserMenu';
import { WorkspaceSelector } from './WorkspaceSelector';

// googlemeet is excluded because Meet recordings are accessed through the
// Google Drive integration's "Meet recordings" tab
const excludedPartners: PartnerType[] = ['rev', 'slack', 'googlechat', 'googlemeet'];

type SideNavigationProps = {
  children?: ReactNode;
};

export function SideNavigation({ children }: SideNavigationProps) {
  const { status } = useUserState();
  const [isCollapsed, setIsCollapsed] = usePref('nav.sidebarCollapsed', false);
  const isMobile = useMediaQuery('(max-width: 768px)');

  const signedIn = status === 'signedIn';

  if (!signedIn) {
    return children;
  }

  return (
    <div className="flex min-h-dvh min-w-0 flex-col">
      <ProdWorkspaceMaintenanceBanner />
      <div className={cx('flex min-h-0 min-w-0 flex-1', isMobile ? 'flex-col' : 'flex-row')}>
        {/* Keep layout tree stable when the viewport crosses the mobile breakpoint so route content
      under <Outlet /> does not remount and lose local state. Mobile uses a top bar in children. */}
        <nav
          aria-hidden={isMobile}
          aria-label="Main navigation"
          className={cx(
            `sticky top-0 h-dvh min-h-0 shrink-0 bg-white dark:bg-[#202124] border-r border-gray-200 dark:border-gray-600 
          flex flex-col transition-all duration-200 ease-in-out z-20`,
            isCollapsed ? 'w-16' : 'w-64',
            'print:hidden',
            isMobile && 'hidden',
          )}
        >
          {!isMobile && (
            <>
              <div className="flex items-center h-16 justify-between p-4 border-b border-gray-200 dark:border-gray-600">
                {!isCollapsed && (
                  <div className="flex items-center gap-3 min-w-0 font-medium">
                    <Link to="/">
                      <RecapLogo />
                    </Link>
                    <EnvironmentTag />
                  </div>
                )}

                <ClarionButton
                  variant="minimal"
                  size="large"
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  tooltip={isCollapsed ? 'Expand navigation' : 'Collapse navigation'}
                  icon={isCollapsed ? SquareChevronRight : SquareChevronLeft}
                  className="-ml-1.5"
                />
              </div>

              <SideNavigationProvider isCollapsed={isCollapsed}>
                <NavigationContent />
              </SideNavigationProvider>
            </>
          )}
        </nav>

        {children}
      </div>
    </div>
  );
}

export function NavigationContent() {
  const isOperator = useIsOperator();
  const organizations = useUserOrganizations();
  const { current: workspace } = useWorkspaces();
  const organizationId = workspace?.organizationId || organizations[0]?.id;
  const isOrgAdmin = useIsOrgAdmin(organizationId);
  const { isCollapsed } = useSideNavigation();

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div
        className={cx(
          'flex-1 overflow-y-auto px-2 py-4',
          isCollapsed ? 'space-y-2 text-center' : 'space-y-4',
        )}
      >
        {isOperator && <OperatorNavigation />}
        <WorkspaceNavigation />
        {isOrgAdmin && organizationId && <OrganizationNavigation organizationId={organizationId} />}
        <NavigationItem to="/help" icon="help">
          Help
        </NavigationItem>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-600 p-2">
        <ActiveTimerButton />
        <FlagOverrides />
        <WorkspaceSelector />
        <UserMenu />
      </div>
    </div>
  );
}

function EnvironmentTag() {
  if (getFlag('localApi')) return <Tag intent="success">LOCAL</Tag>;
  if (getFlag('betaApi')) return <Tag intent="warning">BETA</Tag>;
  return null;
}

function OperatorNavigation() {
  return (
    <>
      <NavigationItem to="/operator" icon="dashboard">
        Operator Dashboard
      </NavigationItem>
      <NavigationItem to="/operator/media-requests/analytics" icon="timeline-bar-chart">
        Media Request Analytics
      </NavigationItem>
    </>
  );
}

function OrganizationNavigation({ organizationId }: { organizationId: string }) {
  return (
    <NavigationGroup
      title="Organization Settings"
      icon="briefcase"
      defaultExpanded={true}
      expandedPrefKey="nav.group.organizationSettingsExpanded"
    >
      <NavigationItem to={`/organizations/${organizationId}/dashboard`} icon="dashboard">
        Dashboard
      </NavigationItem>
      <NavigationItem to={`/organizations/${organizationId}/admins`} icon="people">
        Administrators
      </NavigationItem>
      <NavigationItem to={`/organizations/${organizationId}/analytics`} icon="timeline-bar-chart">
        Analytics
      </NavigationItem>
      <NavigationItem to={`/organizations/${organizationId}/packages`} icon="inbox">
        Packages
      </NavigationItem>
      <NavigationItem to={`/organizations/${organizationId}/invoices`} icon="document">
        Invoices
      </NavigationItem>
      <NavigationItem to={`/organizations/${organizationId}/billing`} icon="credit-card">
        Billing
      </NavigationItem>
    </NavigationGroup>
  );
}

function useReviewQueueCounts() {
  const { user } = useUserState();
  const { current: workspace } = useWorkspaces();
  const workspaceId = workspace?.workspaceId;
  const isContentManager = useIsContentManager();
  const isTranscriptEditor = useIsTranscriptEditor();
  const isOperator = useIsOperator();
  const { data: assignments } = useQuery({
    queryKey: ['user-assignments', user?.uid, workspaceId],
    queryFn: () => getUserAssignments(user?.uid || '', workspaceId || ''),
    enabled: Boolean(user?.uid) && Boolean(workspaceId),
    staleTime: 60_000,
  });

  const { data: createdByAssignments } = useQuery({
    queryKey: ['created-by-assignments', user?.uid, workspaceId],
    queryFn: () => getCreatedByAssignments(user?.uid || '', workspaceId || ''),
    enabled: Boolean(user?.uid) && Boolean(workspaceId),
    staleTime: 60_000,
  });

  const ownPending =
    (assignments || []).filter((a) => a.status !== 'completed' && a.status !== 'cancelled')
      ?.length || 0;

  const reviewRequestsPending =
    (createdByAssignments || []).filter(
      (a) =>
        a.status !== 'completed' &&
        a.status !== 'cancelled' &&
        !(assignments || []).some((own) => own.id === a.id),
    )?.length || 0;

  const pendingAssignments = ownPending + reviewRequestsPending;

  const { data: mediaRequests } = useQuery({
    queryKey: ['mediaRequests', workspaceId, 0],
    queryFn: () =>
      authFetch(
        `/media_requests/workspaces/${workspaceId}?workspaceId=${workspaceId}&limit=20&status=pending`,
      ),
    enabled: isContentManager && Boolean(workspaceId),
  });

  const { data: proposedCorrections } = useQuery({
    queryKey: ['proposedCorrections', workspaceId, 0],
    queryFn: () => authFetch(`${correctionsPath}?workspaceId=${workspaceId}&limit=20`),
    enabled: isTranscriptEditor && Boolean(workspaceId) && !isOperator,
  });

  return {
    pendingAssignments,
    pendingMediaRequests: mediaRequests?.length || 0,
    pendingProposedCorrections: proposedCorrections?.corrections?.length || 0,
  };
}

function WorkspaceNavigation() {
  const { current: workspace } = useWorkspaces();
  const workspaceId = workspace?.workspaceId;
  const isAdmin = useIsAdmin();
  const isContentManager = useIsContentManager();
  const isIntegrationReviewer = useIsIntegrationReviewer();
  const isPricingAllowed = useIsPricingAllowed();
  const isOperator = useIsOperator();
  const { canAccess } = useIntegrationScope();
  const { pendingAssignments, pendingMediaRequests, pendingProposedCorrections } =
    useReviewQueueCounts();

  if (!workspaceId) return null;

  return (
    <>
      <NavigationGroup
        title="Media Library"
        icon="folder-open"
        defaultExpanded={true}
        expandedPrefKey="nav.group.filesExpanded"
      >
        <NavigationItem to={`/workspaces/${workspaceId}/files/recap`}>Recap Files</NavigationItem>
        {partners
          .filter(
            (partner) =>
              !excludedPartners.includes(partner) &&
              getFlag(partner) &&
              workspace?.activeIntegrations?.some(
                (integration) =>
                  integration.platformType === partner ||
                  (partner === 'googledrive' && integration.platformType === 'googlemeet'),
              ) &&
              (isIntegrationReviewer ? canAccess(partner) : true),
          )
          .map((partner) => (
            <NavigationItem key={partner} to={`/workspaces/${workspaceId}/files/${partner}`}>
              {getPartnerLabel(partner)}
            </NavigationItem>
          ))}
      </NavigationGroup>

      {!isIntegrationReviewer && (
        <NavigationItem to={`/workspaces/${workspaceId}/search`} icon="search">
          Search
        </NavigationItem>
      )}

      {!isIntegrationReviewer && getFlag('streams') && (
        <NavigationGroup
          title="Live Streams"
          icon="antenna"
          defaultExpanded={false}
          expandedPrefKey="nav.group.liveStreamsExpanded"
        >
          <NavigationItem to={`/workspaces/${workspaceId}/streams`}>Stream Manager</NavigationItem>
          {isContentManager && (
            <>
              <NavigationItem to={`/workspaces/${workspaceId}/live-transcription/scheduled-events`}>
                Scheduled Events
              </NavigationItem>
              <NavigationItem to={`/workspaces/${workspaceId}/live-transcription/work-orders`}>
                Work Orders
              </NavigationItem>
              <NavigationItem to={`/workspaces/${workspaceId}/live-transcription/analytics`}>
                Live Analytics
              </NavigationItem>
            </>
          )}
        </NavigationGroup>
      )}

      {isContentManager && !isIntegrationReviewer && (
        <NavigationGroup
          title="Review Queue"
          icon="inbox"
          defaultExpanded={true}
          expandedPrefKey="nav.group.reviewQueueExpanded"
        >
          <NavigationItem
            to={`/workspaces/${workspaceId}/corrections`}
            icon="edit"
            badge={pendingProposedCorrections}
          >
            Corrections
          </NavigationItem>
          {isContentManager && (
            <NavigationItem
              to={`/workspaces/${workspaceId}/requests`}
              icon="lifesaver"
              badge={pendingMediaRequests}
            >
              Media Requests
            </NavigationItem>
          )}
          <NavigationItem
            to={`/workspaces/${workspaceId}/assignments`}
            icon="form"
            badge={pendingAssignments}
          >
            Assignments
          </NavigationItem>
        </NavigationGroup>
      )}

      {isAdmin && (
        <NavigationItem to={`/workspaces/${workspaceId}/settings/showcases`} icon="presentation">
          Showcases
        </NavigationItem>
      )}

      {isAdmin && getFlag('captionAudit') && (
        <NavigationItem to={`/workspaces/${workspaceId}/settings/caption-audit`} icon={SearchCheck}>
          Caption Audits
        </NavigationItem>
      )}

      <NavigationGroup
        title="Settings"
        icon="cog"
        defaultExpanded={false}
        expandedPrefKey="nav.group.settingsExpanded"
      >
        <NavigationItem to={`/workspaces/${workspaceId}/settings/general`} icon={Settings2}>
          General
        </NavigationItem>

        {isAdmin && (
          <NavigationItem to={`/workspaces/${workspaceId}/settings/members`} icon="people">
            Members
          </NavigationItem>
        )}

        {(isAdmin || isIntegrationReviewer) && (
          <NavigationItem to={`/workspaces/${workspaceId}/settings/integrations`} icon="link">
            Integrations
          </NavigationItem>
        )}

        {isAdmin && (
          <NavigationItem to={`/workspaces/${workspaceId}/settings/dictionaries`} icon="book">
            Dictionaries
          </NavigationItem>
        )}

        {isContentManager && !isIntegrationReviewer && (
          <NavigationItem
            to={`/workspaces/${workspaceId}/settings/analytics`}
            icon="timeline-bar-chart"
          >
            Analytics
          </NavigationItem>
        )}

        {isContentManager && !isIntegrationReviewer && (
          <NavigationItem
            to={`/workspaces/${workspaceId}/settings/stats`}
            icon="timeline-bar-chart"
          >
            Stats
          </NavigationItem>
        )}

        {isContentManager && !isIntegrationReviewer && getFlag('referrals') && (
          <NavigationItem to={`/workspaces/${workspaceId}/settings/referrals`} icon="gift-box">
            Referrals
          </NavigationItem>
        )}

        {isContentManager && !isIntegrationReviewer && (
          <NavigationItem to={`/workspaces/${workspaceId}/departments`} icon="office">
            Departments
          </NavigationItem>
        )}
        {isContentManager && !isIntegrationReviewer && (
          <NavigationItem to={`/workspaces/${workspaceId}/settings/credit`} icon="credit-card">
            Usage and billing
          </NavigationItem>
        )}

        {isAdmin && !isIntegrationReviewer && isPricingAllowed && (
          <NavigationItem
            to={`/workspaces/${workspaceId}/settings/pricing-calculator`}
            icon="calculator"
          >
            Pricing calculator
          </NavigationItem>
        )}

        {isAdmin && !isIntegrationReviewer && (
          <NavigationItem to={`/workspaces/${workspaceId}/settings/activity-log`} icon="history">
            Activity log
          </NavigationItem>
        )}

        {isOperator && (
          <NavigationItem
            to={`/workspaces/${workspaceId}/settings/speech-operations`}
            icon={Activity}
          >
            Speech operations
          </NavigationItem>
        )}
      </NavigationGroup>
    </>
  );
}
